/**
 * WorkspaceContext
 *
 * - Fetches/caches workspace data and transforms it for landing page, dropdown, and detail views.
 * - workspaceItemsWithPresence.memberData is the source of truth for rendering: isOnline, avatar,
 *   isAdmin, userName. Kept in sync by USER_ONLINE/USER_OFFLINE (WorkspaceRelayHub + UserRelayHub).
 * - currentUserIsOnline reflects the current user's global online state (e.g. for landing page).
 * - Manages current workspace selection, Private workspace auto-creation, and refresh on
 *   workspace/user/ask management events.
 */

// ============= Imports =============

// React hooks and utilities
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';

// APIs
import {
  getUserWorkspace,
  UserWorkspaceResponse,
  UserWorkspace,
  WorkspaceTimeInfo,
  getWorkspacesOrderedByLastUpdate,
} from '@/apis/core/workspace/get/getUserWorkspace';
import { postCreatePersonalWorkspace } from '@/apis/core/workspace/post/postCreatePersonalWorkspace';
import { DEFAULT_AVATAR_FALLBACK } from '@/apis/core/user/get/getUserImage';

// Contexts
import { AuthContextProps, UseAuth } from './AuthContext';

// Utilities
import {
  transformWorkspacesToItems,
  transformAllUserWorkspaces,
  TransformedUserWorkspace,
} from '@/utils/data/transformWorkSpaceItems';
import { transformWorkspacesToDropdown } from '@/mockData/landingPageMockData';

// Hooks
import { useWorkspaceRelayHubHandlers } from '@/hooks/signalR/useWorkspaceRelayHubHandlers';
import { useUserRelayHubHandlers } from '@/hooks/signalR/useUserRelayHubHandlers';
import { useDebug } from '@/contexts/DebugContext';
import { useSignalR } from '@/contexts/signalR';
import { sendMessageToUserUserSession } from '@/apis/core/messaging/userMessageRelayHub';
import { UserRelayHubPresenceMessageType } from '@/handlers/signalR/userRelayHub';

// SignalR handlers
import { WorkspaceRelayHubEvents } from '@/handlers/signalR/workspaceRelayHub';
import { UserRelayHubEvents } from '@/handlers/signalR/userRelayHub';

// ============= Types =============

/**
 * Consolidated member data for a workspace item.
 * Use this instead of parallel arrays (memberUserIds, memberAvatars, etc.) for easier passing and display.
 */
export interface WorkspaceMemberData {
  userName: string;
  userId: string;
  isOnline: boolean;
  isAdmin: boolean;
  avatar: string;
}

/**
 * Workspace item structure for landing page display.
 */
export interface WorkspaceItem {
  id: string;
  title: string;
  lastUsed: string;
  memberAvatars?: string[];
  /** Member user IDs, same order as memberAvatars */
  memberUserIds?: string[];
  /** Member admin/owner flags, same order as memberAvatars */
  memberIsAdmin?: boolean[];
  /** One boolean per memberUserIds: true if that user is online (set from USER_ONLINE/USER_OFFLINE in context) */
  memberIsOnline?: boolean[];
  /** Member display names, same order as memberAvatars */
  memberUserNames?: string[];
  /** Consolidated member data (userName, userId, isOnline, isAdmin, avatar) for easier use */
  memberData?: WorkspaceMemberData[];
  /** Workspace type (Private, Collaborative, Standard) */
  workspaceType?: string;
  /** Number of documents in the workspace data room */
  documentCount?: number;
  /** Number of ask sessions in the workspace */
  askSessionCount?: number;
  /** Number of documents linked to ask sessions */
  documentsLinkedToAsksCount?: number;
  /** Client name when available */
  clientName?: string | null;
  /** Matter name when available */
  matterName?: string | null;
}

/**
 * Workspace group structure for landing page.
 */
export interface WorkspaceGroup {
  title: string;
  items: WorkspaceItem[];
}

/**
 * Dropdown option structure for workspace selector.
 */
export interface WorkspaceDropdownOption {
  label: string;
  value: string;
  icon: React.ReactElement;
}

/**
 * Dropdown group structure for workspace selector.
 */
export interface WorkspaceDropdownGroup {
  label: string;
  options: WorkspaceDropdownOption[];
}

/**
 * WorkspaceContext interface providing workspace management functionality.
 * Exposes workspace data, transformation methods, and real-time update handlers.
 */
export interface WorkspaceContextProps {
  /** Raw workspace data from API */
  workspaces: UserWorkspaceResponse;
  /** Workspaces ordered by last update time */
  workspacesOrderedByLastUpdate: WorkspaceTimeInfo[];
  /** Transformed data for landing page display */
  workspaceItems: WorkspaceGroup[];
  /**
   * Workspace items with presence. Use memberData for isOnline, avatar, isAdmin, userName when
   * rendering workspace items; it is kept in sync by USER_ONLINE/USER_OFFLINE and avoids extra calls.
   */
  workspaceItemsWithPresence: WorkspaceGroup[];
  /** Transformed data for dropdown selector */
  dropdownGroups: WorkspaceDropdownGroup[];
  /** Current selected workspace ID (from route) */
  currentWorkspaceId: string | null;
  /** Loading state indicator */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Actions */
  /** Refreshes workspace data from API */
  refreshWorkspaces: () => Promise<void>;
  /** Sets the current workspace ID */
  setCurrentWorkspaceId: (id: string | null) => void;
  /** Gets a workspace by ID from raw data */
  getWorkspaceById: (id: string) => UserWorkspace | undefined;
  /** Resolves a workspace display name by ID (for user-facing toasts). Returns id if not found. */
  getWorkspaceNameById: (workspaceId: string) => string;
  /** Gets a transformed workspace by ID (includes online status) */
  getTransformedWorkspaceById: (
    id: string,
  ) => TransformedUserWorkspace | undefined;
  /** Updates user online status in transformed workspace data */
  updateUserOnlineStatus: (
    workspaceId: string,
    userId: string,
    isOnline: boolean,
  ) => void;
  /**
   * Whether the current user is considered online (from UserRelayHub USER_ONLINE/USER_OFFLINE).
   * Useful on the landing page where we don't have workspace-specific presence yet.
   */
  currentUserIsOnline: boolean;
  /**
   * Resolves a display name for a user from workspace member data.
   * Used by UserRelayHub handlers (e.g. USER_ONLINE/USER_OFFLINE toasts).
   * Returns userId if not found.
   */
  getDisplayNameForUser: (userId: string, workspaceId?: string) => string;
}

// ============= Constants =============

/**
 * Workspace type constant for Private workspaces.
 */
const WORKSPACE_TYPE_PRIVATE = 'Private' as const;

// ============= Context =============

/**
 * React context for workspace management.
 * Provides workspace data and operations to consuming components.
 */
const WorkspaceContext = createContext<WorkspaceContextProps | undefined>(
  undefined,
);

// ============= Hook =============

/**
 * Hook to access the WorkspaceContext.
 * Must be used within a WorkspaceProvider.
 *
 * @returns WorkspaceContextProps - The workspace context value
 * @throws Error if used outside of WorkspaceProvider
 */
export const useWorkspace = (): WorkspaceContextProps => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

// ============= Provider Component =============

/**
 * WorkspaceProvider Component
 *
 * Provides workspace context to the application tree.
 * Manages workspace state, transformations, and real-time updates.
 *
 * @param props - Component props
 * @param props.children - Child components to render
 * @returns JSX.Element - The provider component
 */
export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // ============= Hooks =============

  const { getAccessToken, isAuthenticated, currentUser } = UseAuth();
  const { invokeHub } = useSignalR();
  const { debug: debugState, canShowDebug } = useDebug();

  // For non-debug users, keep toasts always on but never enable logging.
  const enableToastsForHubs = canShowDebug ? debugState.enableToasts : true;
  const enableLoggingForHubs = canShowDebug && debugState.enableLogging;

  // ============= State =============

  /** Raw workspace data from API */
  const [workspaces, setWorkspaces] = useState<UserWorkspaceResponse>([]);

  /** Transformed workspace items for landing page */
  const [workspaceItems, setWorkspaceItems] = useState<WorkspaceGroup[]>([]);

  /** Transformed dropdown groups for workspace selector */
  const [dropdownGroups, setDropdownGroups] = useState<
    WorkspaceDropdownGroup[]
  >([]);

  /** Current selected workspace ID */
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(
    null,
  );

  /** Loading state */
  const [isLoading, setIsLoading] = useState(false);

  /** Error state */
  const [error, setError] = useState<Error | null>(null);

  /** Transformed workspaces with online status */
  const [transformedWorkspaces, setTransformedWorkspaces] = useState<
    TransformedUserWorkspace[]
  >([]);

  /** Workspaces ordered by last update time */
  const [workspacesOrderedByLastUpdate, setWorkspacesOrderedByLastUpdate] =
    useState<WorkspaceTimeInfo[]>([]);

  /**
   * Whether the current user is online (from UserRelayHub USER_ONLINE/USER_OFFLINE).
   * Used by the landing page and anywhere that needs a single "am I online?" flag.
   */
  const [currentUserIsOnline, setCurrentUserIsOnline] = useState(false);

  /** Ref to track if context has been initialized */
  const isInitializedRef = useRef(false);

  /** Ref to avoid requesting presence multiple times for the same workspace */
  const presenceRequestedForWorkspaceRef = useRef<string | null>(null);

  // ============= Utilities =============

  /**
   * Transforms and sets workspace data in all required formats.
   * This is a helper function to avoid code duplication.
   *
   * @param data - Raw workspace data from API
   * @param getAccessToken - Function to get access token
   */
  const transformAndSetWorkspaceData = useCallback(
    async (
      data: UserWorkspaceResponse,
      getAccessToken: AuthContextProps['getAccessToken'],
    ) => {
      // Set raw workspace data
      setWorkspaces(data);

      // Transform for landing page
      const transformed = await transformWorkspacesToItems(
        data,
        getAccessToken,
      );
      setWorkspaceItems(transformed);

      // Transform for dropdown
      const dropdown = transformWorkspacesToDropdown(data);
      setDropdownGroups(dropdown);

      // Transform for detailed workspace data (with online status)
      const transformedWorkspaces = await transformAllUserWorkspaces(
        data,
        getAccessToken,
      );
      setTransformedWorkspaces(transformedWorkspaces);

      // Transform for workspaces ordered by last update
      const workspacesOrderedByLastUpdate =
        await getWorkspacesOrderedByLastUpdate(data);
      setWorkspacesOrderedByLastUpdate(workspacesOrderedByLastUpdate);
    },
    [],
  );

  /**
   * Checks if a Private workspace exists in the workspace data.
   *
   * @param data - Raw workspace data
   * @returns True if Private workspace exists
   */
  const hasPrivateWorkspace = useCallback(
    (data: UserWorkspaceResponse): boolean => {
      return data.some(
        (workspace) => workspace.workspaceType === WORKSPACE_TYPE_PRIVATE,
      );
    },
    [],
  );

  // ============= Data Fetching =============

  /**
   * Fetches and transforms workspace data from the API.
   * Automatically creates a Private workspace if one doesn't exist.
   * Handles all data transformations for different UI components.
   */
  const fetchWorkspaces = useCallback(async () => {
    if (!isAuthenticated || !getAccessToken) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch workspace data
      const data = await getUserWorkspace({
        getAccessToken: getAccessToken as AuthContextProps['getAccessToken'],
      });

      // Check if user has a Private workspace
      if (!hasPrivateWorkspace(data)) {
        // If no Private workspace exists, create one
        try {
          console.log('📝 No Private workspace found, creating one...');
          await postCreatePersonalWorkspace({
            getAccessToken:
              getAccessToken as AuthContextProps['getAccessToken'],
            body: undefined, // Private workspace doesn't need a body
          });
          console.log('✅ Private workspace created successfully');

          // Fetch workspaces again to get the newly created Private workspace
          const updatedData = await getUserWorkspace({
            getAccessToken:
              getAccessToken as AuthContextProps['getAccessToken'],
          });

          // Transform and set all workspace data
          await transformAndSetWorkspaceData(
            updatedData,
            getAccessToken as AuthContextProps['getAccessToken'],
          );
        } catch (createError) {
          console.error('🔴 Error creating Private workspace:', createError);
          // Continue with existing workspaces even if creation fails
          await transformAndSetWorkspaceData(
            data,
            getAccessToken as AuthContextProps['getAccessToken'],
          );
        }
      } else {
        // Private workspace exists, proceed normally
        await transformAndSetWorkspaceData(
          data,
          getAccessToken as AuthContextProps['getAccessToken'],
        );
      }
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load workspaces');
      setError(error);
      console.error('Failed to load workspaces:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    isAuthenticated,
    getAccessToken,
    hasPrivateWorkspace,
    transformAndSetWorkspaceData,
  ]);

  // ============= Effects =============

  /**
   * Initial fetch when user becomes authenticated.
   * Resets state when user logs out.
   */
  useEffect(() => {
    if (isAuthenticated && !isInitializedRef.current) {
      isInitializedRef.current = true;
      void fetchWorkspaces();
    } else if (!isAuthenticated) {
      // Reset when logged out
      isInitializedRef.current = false;
      setWorkspaces([]);
      setWorkspaceItems([]);
      setDropdownGroups([]);
      setCurrentWorkspaceId(null);
      setTransformedWorkspaces([]);
      setWorkspacesOrderedByLastUpdate([]);
      setCurrentUserIsOnline(false);
    }
  }, [isAuthenticated, fetchWorkspaces]);

  // ============= Event Handlers =============

  /**
   * Updates user online status in transformed workspace data.
   * This is the single source of truth for presence; workspaceItemsWithPresence.memberData
   * is derived from it, so UI should always use memberData (isOnline, avatar, isAdmin, userName).
   * Skips state update when setting online and user is already online to reduce churn.
   */
  const updateUserOnlineStatus = useCallback(
    (workspaceId: string, userId: string, isOnline: boolean) => {
      setTransformedWorkspaces((prev) => {
        let anyChange = false;
        const next = prev.map((ws) => {
          if (ws.id !== workspaceId) return ws;

          const existing = ws.workspaceUsers.userMetadata[userId];
          if (existing) {
            if (existing.isOnline === isOnline) return ws;
            anyChange = true;
            return {
              ...ws,
              workspaceUsers: {
                ...ws.workspaceUsers,
                userMetadata: {
                  ...ws.workspaceUsers.userMetadata,
                  [userId]: { ...existing, isOnline },
                },
              },
            };
          }

          anyChange = true;
          return {
            ...ws,
            workspaceUsers: {
              ...ws.workspaceUsers,
              users: {
                ...ws.workspaceUsers.users,
                [userId]: ws.workspaceUsers.users[userId] ?? false,
              },
              userImages: {
                ...ws.workspaceUsers.userImages,
                [userId]:
                  ws.workspaceUsers.userImages[userId] ??
                  DEFAULT_AVATAR_FALLBACK,
              },
              userMetadata: {
                ...ws.workspaceUsers.userMetadata,
                [userId]: {
                  image:
                    ws.workspaceUsers.userImages[userId] ??
                    DEFAULT_AVATAR_FALLBACK,
                  isOwner: ws.workspaceUsers.users[userId] ?? false,
                  isOnline,
                  userId,
                },
              },
            },
          };
        });
        return anyChange ? next : prev;
      });
    },
    [],
  );

  /**
   * Updates a user's online status in every workspace they belong to.
   * Used when UserRelayHub sends USER_ONLINE/USER_OFFLINE with only userId (e.g. on connect).
   * Skips per-workspace update when setting online and already online to reduce churn.
   */
  const updateUserOnlineStatusInAllWorkspaces = useCallback(
    (userId: string, isOnline: boolean) => {
      setTransformedWorkspaces((prev) => {
        let anyChange = false;
        const next = prev.map((ws) => {
          if (!(userId in ws.workspaceUsers.users)) return ws;

          const existing = ws.workspaceUsers.userMetadata[userId];
          if (existing) {
            if (existing.isOnline === isOnline) return ws;
            anyChange = true;
            return {
              ...ws,
              workspaceUsers: {
                ...ws.workspaceUsers,
                userMetadata: {
                  ...ws.workspaceUsers.userMetadata,
                  [userId]: { ...existing, isOnline },
                },
              },
            };
          }

          anyChange = true;
          return {
            ...ws,
            workspaceUsers: {
              ...ws.workspaceUsers,
              userImages: {
                ...ws.workspaceUsers.userImages,
                [userId]:
                  ws.workspaceUsers.userImages[userId] ??
                  DEFAULT_AVATAR_FALLBACK,
              },
              userMetadata: {
                ...ws.workspaceUsers.userMetadata,
                [userId]: {
                  image:
                    ws.workspaceUsers.userImages[userId] ??
                    DEFAULT_AVATAR_FALLBACK,
                  isOwner: ws.workspaceUsers.users[userId] ?? false,
                  isOnline,
                  userId,
                },
              },
            },
          };
        });
        return anyChange ? next : prev;
      });
    },
    [],
  );

  /**
   * When workspace data is loaded and we have a current user, mark the current user
   * as online in all their workspaces in context. This makes isOnline available
   * everywhere (e.g. landing page, workspace header) without needing to join a
   * workspace SignalR group first. SignalR USER_OFFLINE can still set us offline later.
   */
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.userId || workspaces.length === 0) {
      return;
    }
    updateUserOnlineStatusInAllWorkspaces(currentUser.userId, true);
    setCurrentUserIsOnline(true);
  }, [
    isAuthenticated,
    currentUser?.userId,
    workspaces.length,
    updateUserOnlineStatusInAllWorkspaces,
  ]);

  /**
   * Stable handler for workspace relay hub presence events.
   * Updates transformed workspace data so avatars can show online/offline colour.
   */
  const handleWorkspacePresenceEvent = useCallback(
    (event: string, _data: unknown, parsed: unknown) => {
      const payload = parsed as { workspaceId: string; userId: string };
      const { workspaceId, userId } = payload;

      if (event === WorkspaceRelayHubEvents.USER_ONLINE) {
        if (workspaceId && userId) {
          updateUserOnlineStatus(workspaceId, userId, true);
        }
      } else if (event === WorkspaceRelayHubEvents.USER_OFFLINE) {
        if (workspaceId && userId) {
          updateUserOnlineStatus(workspaceId, userId, false);
        }
      } else if (
        event === WorkspaceRelayHubEvents.USER_CONNECTED_TO_WORKSPACE
      ) {
        // Double-check: connection to workspace also implies user is online
        if (workspaceId && userId) {
          updateUserOnlineStatus(workspaceId, userId, true);
        }
      } else if (
        event === WorkspaceRelayHubEvents.USER_DISCONNECTED_FROM_WORKSPACE
      ) {
        if (workspaceId && userId) {
          updateUserOnlineStatus(workspaceId, userId, false);
        }
      }
    },
    [updateUserOnlineStatus],
  );

  // ============= Helper Functions (needed for workspaceItemsWithPresence and getDisplayNameForUser) =============

  /**
   * Gets a workspace by ID from raw workspace data.
   */
  const getWorkspaceById = useCallback(
    (id: string): UserWorkspace | undefined => {
      return workspaces.find((ws) => ws.id === id);
    },
    [workspaces],
  );

  /**
   * Resolves a workspace display name by ID (for user-facing toasts).
   */
  const getWorkspaceNameById = useCallback(
    (workspaceId: string): string => {
      const ws = workspaces.find((w) => w.id === workspaceId);
      return ws?.title ?? workspaceId;
    },
    [workspaces],
  );

  /**
   * Gets a transformed workspace by ID (includes online status).
   */
  const getTransformedWorkspaceById = useCallback(
    (id: string): TransformedUserWorkspace | undefined => {
      return transformedWorkspaces.find((ws) => ws.id === id);
    },
    [transformedWorkspaces],
  );

  /**
   * Workspace items with presence: memberData is the source of truth for rendering.
   * Use memberData for isOnline, avatar, isAdmin, userName so UI doesn't need extra calls.
   * Always kept in sync via USER_ONLINE/USER_OFFLINE (WorkspaceRelayHub + UserRelayHub).
   */
  /**
   * Workspace items with presence. Used only on the landing page.
   * We omit memberIsOnline so the dashboard does not show online/offline colours
   * (presence is tracked per-workspace; on the landing page we show neutral avatar borders).
   */
  const workspaceItemsWithPresence = useMemo((): WorkspaceGroup[] => {
    return workspaceItems.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        if (!item.memberUserIds?.length) return item;
        const transformed = getTransformedWorkspaceById(item.id);
        const { userData = {} } = transformed?.workspaceUsers ?? {};
        const memberUserNames: string[] = item.memberUserIds.map((userId) => {
          const data = userData[userId];
          return data
            ? `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim()
            : '';
        });
        const memberData: WorkspaceMemberData[] = item.memberUserIds.map(
          (userId, i) => ({
            userId,
            userName: memberUserNames[i] || userId,
            isOnline: false,
            isAdmin: item.memberIsAdmin?.[i] ?? false,
            avatar:
              transformed?.workspaceUsers?.userImages?.[userId] ??
              item.memberAvatars?.[i] ??
              '',
          }),
        );
        return {
          ...item,
          memberUserIds: item.memberUserIds,
          memberUserNames,
          memberData,
          // Intentionally omit memberIsOnline so landing page shows neutral avatar borders
        };
      }),
    }));
  }, [workspaceItems, getTransformedWorkspaceById]);

  /**
   * Resolves a display name for a user from workspace member data (memberData).
   * Used by UserRelayHub handlers for USER_ONLINE/USER_OFFLINE toasts.
   */
  const getDisplayNameForUser = useCallback(
    (userId: string, workspaceId?: string): string => {
      for (const group of workspaceItemsWithPresence) {
        for (const item of group.items) {
          if (workspaceId != null && item.id !== workspaceId) continue;
          const member = item.memberData?.find((m) => m.userId === userId);
          if (member?.userName) return member.userName;
        }
      }
      return userId;
    },
    [workspaceItemsWithPresence],
  );

  // ============= Hub Handlers =============

  /**
   * Workspace relay hub: user presence (USER_ONLINE/USER_OFFLINE etc.).
   * Updates transformedWorkspaces so workspaceItemsWithPresence.memberData.isOnline stays correct.
   */
  useWorkspaceRelayHubHandlers({
    workspaceId: undefined,
    onEventReceived: handleWorkspacePresenceEvent,
    getDisplayNameForUser,
    getWorkspaceNameById,
    currentUserId: currentUser?.userId,
    enableToasts: false,
    enableLogging: enableLoggingForHubs,
  });

  /**
   * User relay hub: USER_ONLINE/USER_OFFLINE update presence (memberData); RECEIVE_MESSAGE for presence discovery (ARE_YOU_THERE / I_AM_HERE).
   * When we receive I_AM_HERE we mark that user online via onPresenceResponse.
   */
  useUserRelayHubHandlers({
    workspaceId: currentWorkspaceId ?? undefined,
    getDisplayNameForUser,
    invokeHub,
    currentUserId: currentUser?.userId,
    currentWorkspaceId: currentWorkspaceId ?? undefined,
    onPresenceResponse: (userId, workspaceId) => {
      if (workspaceId) {
        updateUserOnlineStatus(workspaceId, userId, true);
      } else {
        updateUserOnlineStatusInAllWorkspaces(userId, true);
      }
    },
    onEventReceived: (event, _data, parsed) => {
      const payload = parsed as { workspaceId?: string; userId?: string };

      if (
        event === UserRelayHubEvents.USER_ONLINE ||
        event === UserRelayHubEvents.USER_OFFLINE
      ) {
        const userId = payload?.userId;
        const isOnline = event === UserRelayHubEvents.USER_ONLINE;
        if (userId) {
          if (payload.workspaceId) {
            updateUserOnlineStatus(payload.workspaceId, userId, isOnline);
          } else {
            updateUserOnlineStatusInAllWorkspaces(userId, isOnline);
          }
          if (userId === currentUser?.userId) {
            setCurrentUserIsOnline(isOnline);
          }
        }
      }

      const shouldRefresh =
        event === UserRelayHubEvents.WORKSPACE_ADDED ||
        event === UserRelayHubEvents.USER_ADDED_TO_WORKSPACE ||
        event === UserRelayHubEvents.USER_DELETED_FROM_WORKSPACE ||
        event === UserRelayHubEvents.USER_PROMOTED_TO_OWNER_IN_WORKSPACE ||
        event === UserRelayHubEvents.USER_DEMOTED_FROM_OWNER_IN_WORKSPACE ||
        event === UserRelayHubEvents.USER_REACTIVATED_IN_WORKSPACE ||
        event === UserRelayHubEvents.WORKSPACE_UPDATED;
      if (shouldRefresh) void fetchWorkspaces();
    },
    enableToasts: enableToastsForHubs,
    enableLogging: enableLoggingForHubs,
  });

  /**
   * Workspace relay hub: ask session lifecycle; refresh list when asks change.
   */
  useWorkspaceRelayHubHandlers({
    workspaceId: undefined,
    currentUserId: currentUser?.userId,
    onEventReceived: (event) => {
      const shouldRefresh =
        event === WorkspaceRelayHubEvents.ASK_SESSION_ADDED_TO_WORKSPACE ||
        event === WorkspaceRelayHubEvents.ASK_SESSION_DELETED_FROM_WORKSPACE ||
        event === WorkspaceRelayHubEvents.ASK_SESSION_UPDATED_IN_WORKSPACE;
      if (shouldRefresh) void fetchWorkspaces();
    },
    enableToasts: false,
    enableLogging: false,
  });

  /**
   * When we enter a workspace, request presence from existing members via UserRelayHub.
   * New joiners have no server-side list of who's online; sending ARE_YOU_THERE to each
   * member causes online clients to reply with I_AM_HERE so we can mark them online.
   */
  useEffect(() => {
    if (
      !currentWorkspaceId ||
      !invokeHub ||
      !currentUser?.userId
    ) {
      return;
    }
    const transformed = getTransformedWorkspaceById(currentWorkspaceId);
    const users = transformed?.workspaceUsers?.users;
    if (!users) return;

    const memberIds = Object.keys(users).filter(
      (id) => id !== currentUser.userId
    );
    if (memberIds.length === 0) return;
    if (presenceRequestedForWorkspaceRef.current === currentWorkspaceId) return;

    presenceRequestedForWorkspaceRef.current = currentWorkspaceId;
    memberIds.forEach((memberId) => {
      void sendMessageToUserUserSession({
        invokeHub,
        userId: memberId,
        senderId: currentUser.userId,
        message: {
          type: UserRelayHubPresenceMessageType.ARE_YOU_THERE,
          workspaceId: currentWorkspaceId,
        },
      });
    });
  }, [
    currentWorkspaceId,
    invokeHub,
    currentUser?.userId,
    getTransformedWorkspaceById,
  ]);

  // ============= Context Value =============
  // Memoize so consumers only re-render when presence/data actually changes (not every provider render).
  const value = useMemo<WorkspaceContextProps>(
    () => ({
      workspaces,
      workspacesOrderedByLastUpdate,
      workspaceItems,
      workspaceItemsWithPresence,
      dropdownGroups,
      currentWorkspaceId,
      isLoading,
      error,
      refreshWorkspaces: fetchWorkspaces,
      setCurrentWorkspaceId,
      getWorkspaceById,
      getWorkspaceNameById,
      getTransformedWorkspaceById,
      updateUserOnlineStatus,
      currentUserIsOnline,
      getDisplayNameForUser,
    }),
    [
      workspaces,
      workspacesOrderedByLastUpdate,
      workspaceItems,
      workspaceItemsWithPresence,
      dropdownGroups,
      currentWorkspaceId,
      isLoading,
      error,
      fetchWorkspaces,
      setCurrentWorkspaceId,
      getWorkspaceById,
      getWorkspaceNameById,
      getTransformedWorkspaceById,
      updateUserOnlineStatus,
      currentUserIsOnline,
      getDisplayNameForUser,
    ],
  );

  // ============= Render =============

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
